import React, { useState, useMemo, useEffect } from "react";
import { Search, X, Store } from "lucide-react";
import { useMarketProducts, useMarketGroups } from "@/lib/api";
import Seo from "@/components/common/Seo";
import { cn } from "@/lib/utils";
import SectionHeading from "@/components/common/SectionHeading";
import MarketCard from "@/components/products/MarketCard";

const PAGE = 24;

export default function Market() {
  const { data: products = [], isLoading } = useMarketProducts();
  const { data: groups = [] } = useMarketGroups();
  const [group, setGroup] = useState("Tümü");
  const [query, setQuery] = useState("");
  const [visible, setVisible] = useState(PAGE);

  const filtered = useMemo(() => {
    const q = query.trim().toLocaleLowerCase("tr");
    return products.filter((p) => {
      const groupOk = group === "Tümü" || p.group_key === group;
      const searchOk =
        !q ||
        p.name.toLocaleLowerCase("tr").includes(q) ||
        (p.subcategory || "").toLocaleLowerCase("tr").includes(q) ||
        (p.short_description || "").toLocaleLowerCase("tr").includes(q);
      return groupOk && searchOk;
    });
  }, [products, group, query]);

  // Filtre değişince görünür sayıyı sıfırla
  useEffect(() => {
    setVisible(PAGE);
  }, [group, query]);

  const shown = filtered.slice(0, visible);

  const pill = (active) =>
    cn(
      "inline-flex min-h-[40px] items-center gap-1.5 rounded-sm border px-3.5 font-mono text-xs uppercase tracking-wide transition-colors",
      active
        ? "border-bordo bg-bordo text-cream"
        : "border-dashed border-charcoal/30 text-charcoal/70 hover:border-bordo hover:text-bordo"
    );

  return (
    <div data-testid="market-page" className="bg-cream">
      <Seo
        title="Market & Şarküteri"
        description="Ertan Kasap raflarından özenle seçilmiş peynir, şarküteri, zeytinyağı, kahvaltılık, baharat ve daha fazlası. Sipariş listene ekle, tek tuşla WhatsApp'tan gönder."
      />

      <section className="border-b border-dashed border-bordo/30 pt-16 pb-12">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeading
            as="h1"
            eyebrow="Market & Şarküteri"
            title="Kasabın Seçtiği Lezzetler"
            description="Et reyonunun ötesinde; peynirden zeytinyağına, baharattan kahvaltılığa özenle seçilmiş premium market ürünleri."
          />
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 pb-24 pt-10 sm:px-6 lg:px-8">
        <div className="mb-8">
          <div className="relative mb-6 max-w-md">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-charcoal/40" />
            <input
              data-testid="market-search-input"
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Ürün ara… (örn. peynir, zeytinyağı, reçel)"
              className="w-full rounded-sm border border-dashed border-charcoal/30 bg-paper py-3 pl-10 pr-10 font-mono text-sm text-charcoal placeholder:text-charcoal/35 focus:border-bordo focus:outline-none"
            />
            {query && (
              <button
                data-testid="market-search-clear"
                onClick={() => setQuery("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-charcoal/40 transition-colors hover:text-bordo"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          <span className="mb-3 block font-mono text-xs uppercase tracking-[0.2em] text-charcoal/50">Kategoriler</span>
          <div className="flex flex-wrap gap-2">
            <button data-testid="market-group-all" onClick={() => setGroup("Tümü")} className={pill(group === "Tümü")}>
              <Store className="h-3.5 w-3.5" /> Tümü
            </button>
            {groups.map((g) => (
              <button
                key={g.key}
                data-testid={`market-group-${g.key}`}
                onClick={() => setGroup(g.key)}
                className={pill(group === g.key)}
              >
                {g.label}
                <span className="font-mono text-[10px] text-current/70">({g.count})</span>
              </button>
            ))}
          </div>
        </div>

        <p className="mb-6 font-mono text-xs uppercase tracking-wider text-charcoal/50" data-testid="market-count">
          {filtered.length} ürün listeleniyor
        </p>

        {isLoading ? (
          <div className="border border-dashed border-bordo/30 py-20 text-center font-mono text-sm text-charcoal/50">
            Ürünler yükleniyor…
          </div>
        ) : filtered.length === 0 ? (
          <div className="border border-dashed border-bordo/30 py-20 text-center font-mono text-sm text-charcoal/50">
            Bu filtrelere uygun ürün bulunamadı. Filtreleri değiştirmeyi deneyin.
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
              {shown.map((p) => (
                <MarketCard key={p.slug} product={p} />
              ))}
            </div>
            {visible < filtered.length && (
              <div className="mt-12 text-center">
                <button
                  data-testid="market-load-more"
                  onClick={() => setVisible((v) => v + PAGE)}
                  className="inline-flex items-center justify-center rounded-sm border border-bordo bg-transparent px-8 py-3.5 font-mono text-xs uppercase tracking-[0.2em] text-bordo transition-colors hover:bg-bordo hover:text-cream"
                >
                  Daha Fazla Göster ({filtered.length - visible})
                </button>
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
}
